import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import type { AdminMemberSearchData } from '../../../shared/dto/EntityRepository';
import { searchAdminMembers } from '../../share/api/adminApi';

interface CareerMemberSearchProps {
  selectedMember: AdminMemberSearchData | null;
  disabled?: boolean;
  onSelect: (member: AdminMemberSearchData | null) => void;
}

const CareerMemberSearch: React.FC<CareerMemberSearchProps> = ({
  selectedMember,
  disabled = false,
  onSelect,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AdminMemberSearchData[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const latestSearchId = useRef(0);

  useEffect(() => {
    const normalizedQuery = query.trim();
    const searchId = ++latestSearchId.current;
    if (!normalizedQuery || disabled) {
      setResults([]);
      setIsSearching(false);
      setSearchFailed(false);
      return;
    }

    setIsSearching(true);
    setSearchFailed(false);
    const timer = window.setTimeout(async () => {
      try {
        const response = await searchAdminMembers(normalizedQuery);
        if (latestSearchId.current !== searchId) return;
        setResults(response.data.members ?? []);
      } catch {
        if (latestSearchId.current !== searchId) return;
        setResults([]);
        setSearchFailed(true);
      } finally {
        if (latestSearchId.current === searchId) setIsSearching(false);
      }
    }, 200);

    return () => {
      window.clearTimeout(timer);
      if (latestSearchId.current === searchId) latestSearchId.current += 1;
    };
  }, [query, disabled]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  const selectMember = (member: AdminMemberSearchData) => {
    onSelect(member);
    setQuery('');
    setResults([]);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((current) => Math.max(0, current - 1));
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((current) => Math.min(results.length - 1, current + 1));
      return;
    }
    if (event.key === 'Enter' && results[selectedIndex]) {
      event.preventDefault();
      selectMember(results[selectedIndex]);
    }
  };

  return (
    <Container>
      <Label>Target member</Label>
      {selectedMember && (
        <SelectedMember>
          <MemberIdentity>
            <MemberName>{selectedMember.username}</MemberName>
            <MemberMeta>
              @{selectedMember.handle || '—'} · {selectedMember.aui} · ID {selectedMember.memberId}
            </MemberMeta>
          </MemberIdentity>
          <ChangeButton
            type="button"
            disabled={disabled}
            onClick={() => onSelect(null)}
          >
            Change
          </ChangeButton>
        </SelectedMember>
      )}
      {!selectedMember && (
        <>
          <SearchInput
            value={query}
            disabled={disabled}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search username, handle, or AUI"
            aria-label="Search target member"
            autoFocus
          />
          <Results>
            {isSearching && <Message>Searching...</Message>}
            {!isSearching && searchFailed && <Message>Search failed.</Message>}
            {!isSearching && !searchFailed && query.trim() && results.length === 0 && (
              <Message>No member found.</Message>
            )}
            {!isSearching && results.map((member, index) => (
              <ResultButton
                key={String(member.memberId)}
                type="button"
                $selected={selectedIndex === index}
                onClick={() => selectMember(member)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <span>{member.username}</span>
                <small>@{member.handle || '—'} · {member.aui}</small>
              </ResultButton>
            ))}
          </Results>
        </>
      )}
    </Container>
  );
};

const Container = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Label = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  background: transparent;
  border: none;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  outline: none;
  ${({ theme }) => theme.typography.Body_03_2};
`;

const Results = styled.div`
  max-height: 34vh;
  overflow-y: auto;
  &::-webkit-scrollbar { display: none; }
`;

const ResultButton = styled.button<{ $selected: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 4px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  background: ${({ theme, $selected }) =>
    $selected ? theme.colors.color_Gray_07 : 'transparent'};
  border: none;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_06};
  cursor: pointer;
  text-align: left;
  ${({ theme }) => theme.typography.Body_04};

  small {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.color_Gray_05};
  }
`;

const Message = styled.div`
  padding: 18px 4px;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const SelectedMember = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 0;
  border-top: 0.5px solid ${({ theme }) => theme.colors.color_Gray_05};
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_05};
`;

const MemberIdentity = styled.div`
  min-width: 0;
`;

const MemberName = styled.div`
  overflow: hidden;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ theme }) => theme.typography.Body_03_2};
`;

const MemberMeta = styled.div`
  overflow: hidden;
  margin-top: 3px;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ theme }) => theme.typography.Body_04};
`;

const ChangeButton = styled.button`
  flex-shrink: 0;
  padding: 4px 0;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  background: transparent;
  border: none;
  border-bottom: 0.5px solid currentColor;
  cursor: pointer;
  ${({ theme }) => theme.typography.Body_04};

  &:disabled { cursor: default; opacity: 0.4; }
`;

export default CareerMemberSearch;
